import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import StudentNav from './StudentNav';
import StudentSideNav from './StudentSideNav';
import { useAuth } from '../../context/AuthContext';
import useInactivityLogout from '../../utils/useInactivityLogout';

const TIMEOUT_MS = 10 * 60 * 1000;   // 10 minutes
const WARN_MS = 1 * 60 * 1000;   // warn 1 minute before

const StudentLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(60); // seconds

    const { logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Called 1 minute before auto-logout
    const handleWarn = useCallback(() => {
        setShowWarning(true);
        setCountdown(60);
    }, []);

    // Called when inactivity timer fires
    const handleAutoLogout = useCallback(() => {
        setShowWarning(false);
        logout();
        navigate('/login');
    }, [logout, navigate]);

    // Inactivity hook — tracks all user events
    const { clearTimers } = useInactivityLogout({
        onLogout: handleAutoLogout,
        onWarn: handleWarn,
        timeoutMs: TIMEOUT_MS,
        warnMs: WARN_MS,
    });

    // Countdown ticker when warning is visible
    useEffect(() => {
        if (!showWarning) return;
        if (countdown <= 0) { handleAutoLogout(); return; }
        const tick = setInterval(() => setCountdown(c => c - 1), 1000);
        return () => clearInterval(tick);
    }, [showWarning, countdown, handleAutoLogout]);

    // "I'm still here" button
    const handleStayLoggedIn = useCallback(() => {
        setShowWarning(false);
        setCountdown(60);
        clearTimers();
        // Simulate activity to reset the hook's internal timers
        window.dispatchEvent(new MouseEvent('mousemove'));
    }, [clearTimers]);

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: '#f0fdf4' }}>
            <StudentSideNav
                isOpen={sidebarOpen}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />

            {!isMobile && sidebarOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 998 }}
                    onClick={() => setSidebarOpen(false)} />
            )}

            <div style={{
                flex: 1,
                marginLeft: isMobile ? 0 : (sidebarCollapsed ? 72 : 250),
                minHeight: '100vh', display: 'flex', flexDirection: 'column',
                transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: 0,
                width: isMobile ? '100%' : `calc(100% - ${sidebarCollapsed ? 72 : 250}px)`,
            }}>
                <StudentNav onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
                <main style={{
                    flex: 1,
                    padding: isMobile ? '16px 12px 80px' : '28px 36px 32px',
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '100%',
                }}>
                    <Outlet />
                </main>
            </div>

            {/* ── Inactivity Warning Toast ──────────────────────── */}
            {showWarning && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(15,30,20,0.50)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20,
                    animation: 'fadeIn 0.25s ease',
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 22,
                        padding: '36px 32px',
                        maxWidth: 380,
                        width: '100%',
                        textAlign: 'center',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
                        border: '1.5px solid #c8e6d8',
                        animation: 'scaleIn 0.3s cubic-bezier(0.4,0,0.2,1)',
                    }}>
                        {/* Countdown ring */}
                        <div style={{
                            width: 80, height: 80, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6edba8, #27a065)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 8px 24px rgba(39,160,101,0.28)',
                        }}>
                            <span style={{ fontSize: 28, fontWeight: 900, color: '#fff', fontFamily: 'Outfit, sans-serif' }}>
                                {countdown}
                            </span>
                        </div>

                        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a3326', fontFamily: 'Outfit, sans-serif', margin: '0 0 10px' }}>
                            Still there?
                        </h2>
                        <p style={{ fontSize: 14, color: '#4a7060', fontFamily: 'Outfit, sans-serif', margin: '0 0 24px', lineHeight: 1.6 }}>
                            You've been inactive for a while.<br />
                            You'll be logged out automatically in <strong>{countdown} second{countdown !== 1 ? 's' : ''}</strong>.
                        </p>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={handleStayLoggedIn}
                                style={{
                                    padding: '11px 24px',
                                    borderRadius: 12, border: 'none',
                                    background: 'linear-gradient(135deg, #6edba8, #27a065)',
                                    color: '#fff', fontSize: 14, fontWeight: 700,
                                    fontFamily: 'Outfit, sans-serif',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 14px rgba(39,160,101,0.30)',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                ✅ Yes, keep me in
                            </button>
                            <button
                                onClick={handleAutoLogout}
                                style={{
                                    padding: '11px 24px',
                                    borderRadius: 12,
                                    border: '1.5px solid rgba(220,96,96,0.3)',
                                    background: 'rgba(239,68,68,0.06)',
                                    color: '#c05050', fontSize: 14, fontWeight: 700,
                                    fontFamily: 'Outfit, sans-serif',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                🚪 Logout now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentLayout;
